/**
 * @file 支付状态机 reducer
 * @description 纯函数实现支付流程的状态转移逻辑，由 usePaymentSession 驱动
 */

import type {PaymentState, PaymentEvent} from './types';
import {OrderStatusValue} from '@/services/commerce/entities/payment';

// eslint-disable-next-line complexity
export function paymentReducer(state: PaymentState, event: PaymentEvent): PaymentState {
    switch (event.type) {
        // 启动：仅 idle 态可 START；其他态想重启需先 CANCEL
        case 'START':
            if (state.status === 'idle') return {status: 'creating'};
            return state;

        // 下单成功：creating → qr-loading
        case 'ORDER_CREATED':
            if (state.status === 'creating') return {status: 'qr-loading', order: event.order};
            return state;

        // 下单失败：creating → failed（可重试）
        case 'ORDER_CREATE_FAILED':
            if (state.status === 'creating') {
                return {status: 'failed', reason: '加载失败，请重试', retryable: true};
            }
            return state;

        // 二维码本地渲染完成：qr-loading → qr-ready
        case 'QR_LOADED':
            if (state.status === 'qr-loading') return {status: 'qr-ready', order: state.order};
            return state;

        // 二维码渲染失败（异常）：qr-loading → failed
        case 'QR_LOAD_FAILED':
            if (state.status === 'qr-loading') {
                return {status: 'failed', reason: '二维码渲染失败，请重试', retryable: true};
            }
            return state;

        // 轮询回调：qr-ready 态和 success(rightsIssued=false) 态响应
        case 'POLL_TICK': {
            const s = event.status;

            // success 态但权益未下发：继续轮询等 RIGHTS_ISSUED
            if (state.status === 'success' && !state.rightsIssued) {
                if (s.orderStatus === OrderStatusValue.RIGHTS_ISSUED) {
                    return {...state, rightsIssued: true, result: s.sku};
                }
                return state;
            }

            // 只在 qr-ready 态处理首次支付结果
            if (state.status !== 'qr-ready') return state;

            if (s.orderStatus === OrderStatusValue.PAID) {
                return {status: 'success', order: state.order, result: s.sku, rightsIssued: false};
            }
            if (s.orderStatus === OrderStatusValue.RIGHTS_ISSUED) {
                return {status: 'success', order: state.order, result: s.sku, rightsIssued: true};
            }
            if (s.orderStatus === OrderStatusValue.FAILED) {
                return {status: 'failed', reason: '支付失败，请重试', retryable: true};
            }
            if (s.orderStatus === OrderStatusValue.REFUNDED) {
                return {status: 'qr-expired', order: state.order};
            }
            return state; // PENDING：保持原态
        }

        // 本地倒计时到时：qr-loading / qr-ready → qr-expired
        case 'EXPIRED':
            if (state.status === 'qr-loading' || state.status === 'qr-ready') {
                return {status: 'qr-expired', order: state.order};
            }
            return state;

        // 用户手动刷新二维码 / 失败重试：qr-expired / failed → creating
        case 'REFRESH_QR':
            if (state.status === 'qr-expired' || state.status === 'failed') {
                return {status: 'creating'};
            }
            return state;

        // 切换 SKU：一律重新走下单流程（包括 success 态后换购新 SKU）
        case 'SWITCH_SKU':
            return {status: 'creating'};

        // 取消：任何态都可迁回 idle（包括 success，关闭弹窗 = 重置会话）
        case 'CANCEL':
            return {status: 'idle'};

        default:
            return state;
    }
}
